import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FadeUp } from '../components/Reveal';

const MATERIE = [
  'Diritto Civile',
  'Diritto Commerciale',
  'Contrattualistica',
  'Diritto Immobiliare',
  'Diritto delle Locazioni',
  'Affitti d\u2019Azienda',
  'Cessioni d\u2019Azienda',
  'Diritto del Lavoro',
  'Diritto delle Successioni',
  'Diritto di Famiglia',
  'Diritto Internazionale',
  'Recupero Crediti',
  'Esecuzioni Civili',
  'Responsabilit\u00e0 Medica',
];

const AMBITI_PROCESSUALI = [
  'Assistenza stragiudiziale',
  'Organi giurisdizionali di merito \u2014 Tribunali, Corti d\u2019Appello',
  'Giurisdizioni superiori',
  'Negoziazione assistita',
  'Mediazione civile \u2014 obbligatoria e facoltativa',
  'Arbitrato civile \u2014 rituale e irrituale, nazionale e internazionale',
];

const APPROCCIO = [];

const PERCHE = [];

const PRESS = [];

export default function ItalianHome() {
  const [mapLoaded, setMapLoaded] = useState(false);

  // The Italian institutional site carries the principal's name and Person
  // schema; the international site must not (brand separation rule).
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'LegalService',
    name: 'Studio Legale Avv. Christian Belliardo',
    url: 'https://belliardo-international.com/it',
    telephone: '+39 045 8011276',
    email: 'avvocati@studiobelliardo.it',
    areaServed: 'IT',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Salita Fontana del Ferro, 24',
      addressLocality: 'Verona',
      postalCode: '37129',
      addressCountry: 'IT',
    },
    founder: { '@type': 'Person', name: 'Avv. Christian Belliardo', jobTitle: 'Avvocato in Verona' },
  };

  return (
    <div className="relative bg-ink-950 text-white min-h-screen" data-testid="it-home">
      <Helmet htmlAttributes={{ lang: 'it' }}>
        <title>Avv. Christian Belliardo — Studio Legale in Verona</title>
        <meta name="description" content="Studio Legale Avv. Christian Belliardo in Verona. Consulenza e assistenza legale in diritto civile, commerciale, immobiliare, del lavoro, successioni e nelle principali aree del diritto, in ogni grado di giudizio." />
        <link rel="canonical" href="https://belliardo-international.com/it" />
        <meta property="og:title" content="Avv. Christian Belliardo — Studio Legale in Verona" />
        <meta property="og:description" content="Studio Legale Avv. Christian Belliardo in Verona. Consulenza e assistenza legale." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://belliardo-international.com/it" />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      {/* Top nav */}
      <header className="fixed top-0 inset-x-0 z-40 border-b border-white/8 backdrop-blur-lg bg-ink-950/70">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <Link to="/it" className="font-serif text-xl md:text-2xl text-white leading-none">
            Belliardo<span className="font-mono text-white/40 text-[10px] ml-3 uppercase tracking-[0.25em]">Studio Legale · Verona</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#studio" className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60 hover:text-white">Studio</a>
            <a href="#materie" className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60 hover:text-white">Materie</a>
            <a href="#modalita" className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60 hover:text-white">Modalit&agrave;</a>
            <a href="#contatti" className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60 hover:text-white">Contatti</a>
          </nav>
          <a href="tel:+390458011276" className="hidden sm:block font-mono text-[11px] uppercase tracking-[0.25em] text-white/80">Tel. 045.8011276</a>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-[80svh] pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden" data-testid="it-hero">
        <div className="absolute top-0 right-0 h-full w-1 bg-oxblood/40" />
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.2 }}
                      className="flex items-center gap-4 text-white/50 mb-8">
            <span className="h-px w-8 bg-oxblood" />
            <span className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.3em]">Studio Legale · Verona</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
            className="font-serif font-light text-white text-[10vw] md:text-[7vw] lg:text-[5.4vw] leading-[0.98] tracking-[-0.015em] max-w-5xl">
            Avv. Christian Belliardo
          </motion.h1>
          <FadeUp delay={0.6} className="mt-8 max-w-2xl">
            <p className="text-lg md:text-xl text-white/75 leading-relaxed">
              Consulenza e assistenza legale in ambito civile, commerciale e societario.
              Studio con sede in Verona. L&apos;Avv. Christian Belliardo è iscritto all&apos;Albo degli Avvocati di Verona.
            </p>
          </FadeUp>
          <FadeUp delay={0.75} className="mt-10 flex flex-wrap gap-6 items-center">
            <a href="#contatti" data-testid="it-hero-cta"
               className="inline-flex items-center gap-3 bg-white text-black px-6 py-3.5 text-[11px] font-mono uppercase tracking-[0.25em] hover:bg-oxblood hover:text-white transition-colors duration-300">
              Richiedi un appuntamento →
            </a>
            <a href="tel:+390458011276" data-testid="it-hero-phone" className="font-mono text-sm text-white/80 hover-underline">
              Tel. 045.8011276
            </a>
          </FadeUp>
        </div>
      </section>

      {/* Studio */}
      <section id="studio" className="relative py-20 md:py-32 border-t border-white/10" data-testid="it-section-studio">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-12">
          <div className="col-span-12 lg:col-span-4">
            <div className="flex items-baseline gap-4 mb-6 sticky top-24">
              <span className="font-serif text-white/40 text-4xl font-light">I.</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Lo Studio</span>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-8 space-y-6 text-white/85">
            <FadeUp>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-light leading-tight tracking-tight mb-6">
                Lo Studio Legale
              </h2>
            </FadeUp>
            <FadeUp delay={0.1}>
              <p className="text-base md:text-lg leading-relaxed">
                Lo Studio Legale Avv. Christian Belliardo ha sede in Verona e opera nei principali ambiti
                del diritto civile, commerciale e societario. L&apos;attivit&agrave; professionale &egrave;
                svolta con riservatezza e con aggiornamento continuo sulle evoluzioni normative e giurisprudenziali.
              </p>
            </FadeUp>
            <FadeUp delay={0.15}>
              <p className="text-base md:text-lg leading-relaxed">
                Lo Studio si avvale della collaborazione stabile di professionisti — interni ed esterni —
                con esperienza nelle materie contigue al diritto civile, per un&apos;assistenza
                coordinata quando la fattispecie lo richiede.
              </p>
            </FadeUp>
            <FadeUp delay={0.2}>
              <p className="text-base md:text-lg leading-relaxed">
                Sotto il profilo processuale, lo Studio assicura la difesa dell&apos;assistito in
                ogni grado di giudizio. L&apos;Avv. Christian Belliardo è iscritto all&apos;albo speciale
                per il patrocinio dinanzi alla Corte di Cassazione e alle altre giurisdizioni superiori.
              </p>
            </FadeUp>
            <FadeUp delay={0.25}>
              <p className="text-base md:text-lg leading-relaxed pt-4 border-t border-white/10 text-white/75">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 block mb-2">— Incarichi</span>
                L&apos;Avv. Christian Belliardo &egrave; altres&igrave; Custode Giudiziario nelle procedure
                esecutive immobiliari presso il Tribunale di Verona.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Studio Photo — editorial band */}
      <section className="relative border-t border-white/10 overflow-hidden bg-ink-950" data-testid="it-section-photo">
        <div className="relative w-full aspect-[21/9] md:aspect-[21/8] max-h-[720px]">
          <img
            src="/studio-belliardo.jpg"
            alt="Studio Legale Belliardo — Verona"
            className="w-full h-full object-cover contrast-105 brightness-95"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/10 to-transparent" />
          <div className="absolute bottom-6 md:bottom-10 left-6 md:left-10 max-w-md">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60">— Studio</div>
            <div className="font-serif italic text-white/80 text-lg md:text-xl mt-2 leading-snug">
              Salita Fontana del Ferro, 24 — Verona.
            </div>
          </div>
        </div>
      </section>

      {/* Materie */}
      <section id="materie" className="relative py-20 md:py-32 border-t border-white/10 bg-ink-900" data-testid="it-section-materie">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="flex items-baseline gap-4 mb-10">
            <span className="font-serif text-white/40 text-4xl font-light">II.</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Aree di attivit&agrave;</span>
          </div>
          <FadeUp>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-light leading-tight tracking-tight max-w-4xl mb-14">
              Materie trattate
            </h2>
          </FadeUp>
          <div className="border-y border-white/10 divide-y divide-white/10 max-w-3xl">
            {MATERIE.map((m, i) => (
              <FadeUp key={m} delay={Math.min(i * 0.02, 0.3)}>
                <div className="py-4 md:py-5 flex items-baseline gap-6" data-testid={`it-materia-${i}`}>
                  <span className="font-mono text-[10px] text-white/40 w-8 shrink-0">{String(i + 1).padStart(2, '0')}</span>
                  <span className="font-serif text-lg md:text-xl text-white/90 leading-snug">{m}</span>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Modalità di assistenza */}
      <section id="modalita" className="relative py-20 md:py-32 border-t border-white/10" data-testid="it-section-modalita">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="flex items-baseline gap-4 mb-10">
            <span className="font-serif text-white/40 text-4xl font-light">III.</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Modalit&agrave; di assistenza</span>
          </div>
          <div className="border-y border-white/10 divide-y divide-white/10 max-w-3xl">
            {AMBITI_PROCESSUALI.map((m, i) => (
              <FadeUp key={m} delay={Math.min(i * 0.03, 0.3)}>
                <div className="py-4 md:py-5 flex items-baseline gap-6" data-testid={`it-modalita-${i}`}>
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 w-10 shrink-0">{['I','II','III','IV','V','VI'][i]}.</span>
                  <span className="font-serif text-lg md:text-xl text-white/90 leading-snug">{m}</span>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Contatti */}
      <section id="contatti" className="relative py-20 md:py-32 border-t border-white/10 bg-ink-900" data-testid="it-section-contatti">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-12">
          <div className="col-span-12 lg:col-span-6">
            <FadeUp>
              <div className="flex items-baseline gap-4 mb-6">
                <span className="font-serif text-white/40 text-4xl font-light">IV.</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Contatti</span>
              </div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white font-light leading-tight tracking-tight">
                Come prendere appuntamento
              </h2>
              <p className="mt-6 text-white/70 max-w-md leading-relaxed">
                Per un primo colloquio &egrave; possibile scrivere ai recapiti indicati oppure telefonare
                allo Studio negli orari di ufficio. Lo Studio riscontra ordinariamente entro
                48 ore lavorative.
              </p>
            </FadeUp>
            <FadeUp delay={0.15} className="mt-10 divide-y divide-white/10 border-y border-white/10">
              {[
                ['Sede', 'Salita Fontana del Ferro n. 24\u00a0—\u00a037129 Verona (VR)'],
                ['Telefono', <a key="t" href="tel:+390458011276" className="hover-underline">045.8011276</a>],
                ['Email', <a key="e" href="mailto:avvocati@studiobelliardo.it" className="hover-underline">avvocati@studiobelliardo.it</a>],
                ['PEC', <a key="p" href="mailto:avvchristianbelliardo@ordineavvocativrpec.it" className="hover-underline break-all">avvchristianbelliardo@ordineavvocativrpec.it</a>],
              ].map(([k, v]) => (
                <div key={k} className="py-4 grid grid-cols-12 gap-4">
                  <div className="col-span-12 md:col-span-3">
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">— {k}</span>
                  </div>
                  <div className="col-span-12 md:col-span-9 text-white/85 text-base leading-relaxed whitespace-pre-line">{v}</div>
                </div>
              ))}
            </FadeUp>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:pt-6">
            <FadeUp delay={0.2}>
              <div className="relative overflow-hidden border border-white/10 h-[420px] md:h-[500px]">
                {mapLoaded ? (
                  <iframe
                    title="Sede Studio Legale Belliardo — Verona"
                    src="https://maps.google.com/maps?q=Salita%20Fontana%20del%20Ferro%2024%2C%2037129%20Verona&z=16&output=embed"
                    className="absolute inset-0 w-full h-full grayscale contrast-110 opacity-90"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 bg-ink-900 px-8 text-center">
                    <span className="font-serif text-2xl text-white/90 leading-snug">Salita Fontana del Ferro, 24 — Verona</span>
                    <span className="text-white/50 text-xs leading-relaxed max-w-[340px]">
                      La mappa è fornita da Google Maps. Cliccando su &quot;Carica la mappa&quot;, i contenuti saranno caricati da Google,
                      che potrà trattare dati di utilizzo secondo la propria informativa.
                    </span>
                    <button type="button" onClick={() => setMapLoaded(true)} data-testid="it-load-map"
                      className="font-mono text-[11px] uppercase tracking-[0.25em] border border-white/25 px-6 py-3 text-white hover:bg-white hover:text-black transition-colors duration-300">
                      Carica la mappa →
                    </button>
                  </div>
                )}
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative bg-ink-950 border-t border-white/10 py-14" data-testid="it-footer">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-[11px] font-mono uppercase tracking-[0.2em] text-white/45">
            <div>P.IVA 03995680232</div>
            <div>Ordine Avvocati Verona N. 2726</div>
            <div className="md:text-right">Tel. 045.8011276</div>
            <div className="md:col-span-3 text-white/40 mt-4">
              Polizza RC Professionale n. M11635296 · ITAS Mutua · Ultimo rinnovo 30/01/2026
            </div>
            <div className="md:col-span-3 mt-4 flex gap-8 text-white/40">
              <Link to="/it/privacy" data-testid="it-footer-privacy-link" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/it/cookie-policy" data-testid="it-footer-cookie-link" className="hover:text-white transition-colors">Cookie Policy</Link>
            </div>
          </div>
          <div className="mt-8 text-[11px] text-white/35 normal-case tracking-normal font-sans leading-relaxed">
            Le informazioni contenute in questo sito hanno finalit&agrave; puramente informative e non
            costituiscono consulenza legale n&eacute; sollecitazione professionale, ai sensi degli artt. 35, 17 e 12
            del Codice Deontologico Forense e della L. 247/2012.
          </div>
        </div>
      </footer>
    </div>
  );
}
